/**
 * "Risk Flags & Alerts" card (design §7): pale red /
 * pale amber 50/50 panels; each row is a flag icon + message text. Recorded
 * deviation vs v1 (RiskFlagsSection.tsx:33-37): the severity-level Badge is
 * dropped — the design shows icon + message only. Each panel renders only
 * when its list is non-empty; the whole card skips when both are empty
 * (v1 parity). The page applies the kind gate at the render site via
 * shouldShowRiskFlags (full always; pre-screen applicants only when flags
 * exist; pre-screen guarantors never).
 */
import { FlagTriangleRight } from 'lucide-react'
import { deriveRiskFlagLists } from '#app/utils/application-detail-v2/report-display'
import { cn } from '#app/utils/misc'
import { DETAIL_CARD, SectionIcon } from '../primitives'

function FlagPanel({
	heading,
	messages,
	panelClass,
	headingClass,
	iconClass,
}: {
	heading: string
	messages: string[]
	panelClass: string
	headingClass: string
	iconClass: string
}) {
	return (
		<div className={cn('rounded-2xl p-6', panelClass)}>
			<h3 className={cn('text-[17px] font-semibold', headingClass)}>
				{heading}
			</h3>
			<ul className="mt-4 space-y-4">
				{messages.map((message, idx) => (
					<li
						key={idx}
						className="flex items-start gap-2.5 text-[15px] text-[#747474]"
					>
						<FlagTriangleRight
							aria-hidden="true"
							className={cn('mt-1 size-4 shrink-0', iconClass)}
						/>
						<span className="flex-1">{message}</span>
					</li>
				))}
			</ul>
		</div>
	)
}

/** The pale-red / pale-amber 50/50 panel grid — the shared flag visual.
 * Used inside the standalone card below AND the combined page's Individual
 * Flags accordion, so a member's flags read identically to the combined ones.
 * Each panel renders only when its list is non-empty. */
export function RiskFlagPanelsBody({
	red,
	yellow,
	className,
}: {
	red: string[]
	yellow: string[]
	className?: string
}) {
	return (
		<div
			className={cn(
				'grid grid-cols-1 items-start gap-6 lg:grid-cols-2',
				className,
			)}
		>
			{red.length > 0 && (
				<FlagPanel
					heading="Red Flags"
					messages={red}
					panelClass="bg-[#FFF7F7]"
					headingClass="text-[#EF4343]"
					iconClass="text-[#EF4343]"
				/>
			)}
			{yellow.length > 0 && (
				<FlagPanel
					heading="Yellow Flags"
					messages={yellow}
					panelClass="bg-[#F59F0A0D]"
					headingClass="text-[#F59F0A]"
					iconClass="text-[#F59F0A]"
				/>
			)}
		</div>
	)
}

/** Presentational card shared by the individual page (derived wrapper
 * below) and the combined page (typed family lists). */
export function RiskFlagPanels({
	red,
	yellow,
	title = 'Risk Flags & Alerts',
}: {
	red: string[]
	yellow: string[]
	title?: string
}) {
	if (red.length === 0 && yellow.length === 0) return null

	return (
		// `id` is the scroll target for the header's "View Risk Factors" link
		// (Guarantor Recommended popover). scroll-mt clears the sticky top bar so
		// the heading isn't hidden behind it after the scroll.
		<section
			id="risk-flags"
			className={cn(DETAIL_CARD, 'scroll-mt-28 p-6 sm:p-7')}
		>
			<div className="flex items-center gap-3">
				<SectionIcon src="/img/risk-flag-alerts.svg" />
				<h2 className="text-[19px] font-semibold text-[#32302F]">{title}</h2>
			</div>
			<RiskFlagPanelsBody red={red} yellow={yellow} className="mt-5" />
		</section>
	)
}

export function RiskFlags({
	aiReportData,
}: {
	aiReportData: Record<string, unknown> | null
}) {
	const { red, yellow } = deriveRiskFlagLists(aiReportData)
	return <RiskFlagPanels red={red} yellow={yellow} />
}
